import { PlanTrailActionContext, getMap } from "./types";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { DeleteSelectedMarkersCommand } from "../../undo/commands/DeleteSelectedMarkersCommand";

const HIGHLIGHT_SOURCE = 'area-selection-highlights';
const HIGHLIGHT_LAYER = 'area-selection-highlights-layer';

// Module-level references so exitAreaSelectMode can clean up handlers set in enterAreaSelectMode
let _canvasMouseDownHandler: ((e: MouseEvent) => void) | null = null;
let _docMouseMoveHandler: ((e: MouseEvent) => void) | null = null;
let _docMouseUpHandler: ((e: MouseEvent) => void) | null = null;
let _docKeyDownHandler: ((e: KeyboardEvent) => void) | null = null;
let _selectionBox: HTMLDivElement | null = null;

interface Bbox {
  minLng: number;
  maxLng: number;
  minLat: number;
  maxLat: number;
}

// Collect intermediate control points (never the first or last marker) of the ACTIVE
// route that fall within the bounding box. Area selection must only ever touch the
// route the user is currently editing — not every route in the hike.
export function collectMarkersInBbox(
  routes: Record<string, { markers: Array<{ markerGeometry: { coordinates: number[] } }> }>,
  activeRouteKey: string | null,
  bbox: Bbox,
): Array<{ routeKey: string; markerIndex: number }> {
  const found: Array<{ routeKey: string; markerIndex: number }> = [];
  const activeRoute = activeRouteKey ? routes[activeRouteKey] : null;
  if (!activeRouteKey || !activeRoute) return found;

  for (let i = 1; i < activeRoute.markers.length - 1; i++) {
    const [lng, lat] = activeRoute.markers[i].markerGeometry.coordinates;
    if (lng >= bbox.minLng && lng <= bbox.maxLng && lat >= bbox.minLat && lat <= bbox.maxLat) {
      found.push({ routeKey: activeRouteKey, markerIndex: i });
    }
  }
  return found;
}

function removeHighlightLayer() {
  const map = getMap();
  if (!map) return;
  try {
    if (map.getLayer(HIGHLIGHT_LAYER)) map.removeLayer(HIGHLIGHT_LAYER);
    if (map.getSource(HIGHLIGHT_SOURCE)) map.removeSource(HIGHLIGHT_SOURCE);
  } catch (_) {}
}

function cleanupDomHandlers() {
  const map = getMap();
  const canvas = map?.getCanvas();

  if (canvas && _canvasMouseDownHandler) {
    canvas.removeEventListener('mousedown', _canvasMouseDownHandler);
    _canvasMouseDownHandler = null;
  }
  if (_docMouseMoveHandler) {
    document.removeEventListener('mousemove', _docMouseMoveHandler);
    _docMouseMoveHandler = null;
  }
  if (_docMouseUpHandler) {
    document.removeEventListener('mouseup', _docMouseUpHandler);
    _docMouseUpHandler = null;
  }
  if (_docKeyDownHandler) {
    document.removeEventListener('keydown', _docKeyDownHandler);
    _docKeyDownHandler = null;
  }
  if (_selectionBox) {
    _selectionBox.remove();
    _selectionBox = null;
  }
}

export const selectionActions = {
  enterAreaSelectMode({ state, commit, dispatch }: PlanTrailActionContext) {
    if (state.isAreaSelecting) return;

    const map = getMap();
    if (!map) return;

    commit('setAreaSelecting', true);
    map.dragPan.disable();
    map.boxZoom.disable();

    const canvas = map.getCanvas();
    canvas.style.cursor = 'crosshair';

    // Inject the selection rectangle div into the map container
    const container = map.getContainer() as HTMLElement;
    const box = document.createElement('div');
    box.style.cssText = [
      'position:absolute',
      'border:2px dashed #fddf00',
      'background:rgba(253,223,0,0.1)',
      'pointer-events:none',
      'display:none',
      'z-index:400',
      'border-radius:2px',
    ].join(';');
    container.appendChild(box);
    _selectionBox = box;

    let startPx: { x: number; y: number } | null = null;

    const onMouseMove = (e: MouseEvent) => {
      if (!startPx || !_selectionBox) return;
      const rect = canvas.getBoundingClientRect();
      const currX = e.clientX - rect.left;
      const currY = e.clientY - rect.top;
      const minX = Math.min(startPx.x, currX);
      const minY = Math.min(startPx.y, currY);
      _selectionBox.style.left = `${minX}px`;
      _selectionBox.style.top = `${minY}px`;
      _selectionBox.style.width = `${Math.abs(currX - startPx.x)}px`;
      _selectionBox.style.height = `${Math.abs(currY - startPx.y)}px`;
      _selectionBox.style.display = 'block';
    };

    const onMouseUp = (e: MouseEvent) => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      _docMouseMoveHandler = null;
      _docMouseUpHandler = null;

      if (!startPx || !_selectionBox) return;
      _selectionBox.style.display = 'none';

      const rect = canvas.getBoundingClientRect();
      const endX = e.clientX - rect.left;
      const endY = e.clientY - rect.top;
      const minPx = { x: Math.min(startPx.x, endX), y: Math.min(startPx.y, endY) };
      const maxPx = { x: Math.max(startPx.x, endX), y: Math.max(startPx.y, endY) };
      startPx = null;

      // Require at least a 5px drag to count as intentional selection
      if (maxPx.x - minPx.x < 5 || maxPx.y - minPx.y < 5) return;

      // Convert pixel corners to geographic bounding box
      const sw = map.unproject([minPx.x, maxPx.y]);
      const ne = map.unproject([maxPx.x, minPx.y]);
      const bboxMinLng = Math.min(sw.lng, ne.lng);
      const bboxMaxLng = Math.max(sw.lng, ne.lng);
      const bboxMinLat = Math.min(sw.lat, ne.lat);
      const bboxMaxLat = Math.max(sw.lat, ne.lat);

      // Collect intermediate markers (never first or last) within bbox — ACTIVE ROUTE ONLY
      const found = collectMarkersInBbox(state.routes, state.activeRouteKey, {
        minLng: bboxMinLng,
        maxLng: bboxMaxLng,
        minLat: bboxMinLat,
        maxLat: bboxMaxLat,
      });

      if (found.length === 0) return;

      commit('setAreaSelectedMarkers', found);

      // Show yellow highlight circles on the selected markers
      const features = found.map(({ routeKey, markerIndex }) => {
        const marker = state.routes[routeKey]?.markers[markerIndex];
        if (!marker) return null;
        return {
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: marker.markerGeometry.coordinates.slice(0, 2) },
          properties: {},
        };
      }).filter(Boolean);

      removeHighlightLayer();
      map.addSource(HIGHLIGHT_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: features as any },
      });
      map.addLayer({
        id: HIGHLIGHT_LAYER,
        type: 'circle',
        source: HIGHLIGHT_SOURCE,
        paint: {
          'circle-radius': 10,
          'circle-color': '#fddf00',
          'circle-opacity': 0.5,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fddf00',
          'circle-stroke-opacity': 1,
        },
      });
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      // Prevent map from receiving this event (would add a control point)
      e.preventDefault();
      e.stopPropagation();
      const rect = canvas.getBoundingClientRect();
      startPx = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      _docMouseMoveHandler = onMouseMove;
      _docMouseUpHandler = onMouseUp;
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dispatch('exitAreaSelectMode');
    };

    _canvasMouseDownHandler = onMouseDown;
    _docKeyDownHandler = onKeyDown;
    canvas.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
  },

  exitAreaSelectMode({ commit }: PlanTrailActionContext) {
    const map = getMap();
    if (map) {
      map.dragPan.enable();
      map.boxZoom.enable();
      map.getCanvas().style.cursor = '';
    }
    removeHighlightLayer();
    cleanupDomHandlers();
    commit('clearAreaSelection');
  },

  async deleteAreaSelectedMarkers({ state, commit, dispatch }: PlanTrailActionContext) {
    if (state.areaSelectedMarkers.length === 0) return;

    // Group selected marker indices by route
    const byRoute: Record<string, number[]> = {};
    for (const { routeKey, markerIndex } of state.areaSelectedMarkers) {
      if (!byRoute[routeKey]) byRoute[routeKey] = [];
      byRoute[routeKey].push(markerIndex);
    }

    const routesBefore: Record<string, { markers: any[]; lineStrings: any[] }> = {};
    const routesAfter: Record<string, { markers: any[]; lineStrings: any[] }> = {};

    for (const [routeKey, selectedIndices] of Object.entries(byRoute)) {
      const route = state.routes[routeKey];
      if (!route || route.lineStrings.length === 0) continue;

      routesBefore[routeKey] = {
        markers: JSON.parse(JSON.stringify(route.markers)),
        lineStrings: JSON.parse(JSON.stringify(route.lineStrings)),
      };

      const selectedSet = new Set(selectedIndices);

      // Always preserve first and last marker of every route
      const keptIndices: number[] = [];
      for (let i = 0; i < route.markers.length; i++) {
        if (!selectedSet.has(i) || i === 0 || i === route.markers.length - 1) {
          keptIndices.push(i);
        }
      }

      if (keptIndices.length === route.markers.length) continue;

      const newMarkers = keptIndices.map(i => route.markers[i]);
      const newLineStrings: LineStringFeature[] = [];

      for (let k = 0; k < keptIndices.length - 1; k++) {
        const fromIdx = keptIndices[k];
        const toIdx = keptIndices[k + 1];

        if (toIdx - fromIdx === 1) {
          // Adjacent kept markers — reuse the existing linestring
          newLineStrings.push(route.lineStrings[fromIdx]);
        } else {
          // Removed markers in between — merge the spanning linestrings
          let mergedCoords: number[][] = [];
          for (let lsIdx = fromIdx; lsIdx < toIdx; lsIdx++) {
            const coords = route.lineStrings[lsIdx].lineStringGeometry.coordinates;
            mergedCoords = mergedCoords.length === 0
              ? [...coords]
              : [...mergedCoords, ...coords.slice(1)];
          }
          const merged = LineStringFeature.coordsOnlyFeature(mergedCoords);
          merged.lineStringProperties = {
            ...route.lineStrings[fromIdx].lineStringProperties,
            id: generateID(),
          };
          newLineStrings.push(merged);
        }
      }

      route.markers = newMarkers;
      route.lineStrings = newLineStrings;
      route.updated_at = new Date();

      routesAfter[routeKey] = {
        markers: JSON.parse(JSON.stringify(route.markers)),
        lineStrings: JSON.parse(JSON.stringify(route.lineStrings)),
      };
    }

    if (Object.keys(routesAfter).length === 0) {
      dispatch('exitAreaSelectMode');
      return;
    }

    state.undoManager.push(new DeleteSelectedMarkersCommand({ routesBefore, routesAfter }));
    dispatch('exitAreaSelectMode');
    await dispatch('updateRouteAndMarkers');
  },
};
